import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { UserAPI } from '../../api/UserAPI';
import { useSelector } from 'react-redux';
import defaultProfileImg1 from '../../assets/images/default_profile_img.png'
import defaultProfileImg2 from '../../assets/images/profile-picture.png'
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { AccordionDetails, Drawer, Menu, Avatar, Chip, Box, OutlinedInput, Select, FormControl, InputLabel, MenuItem, Button, Accordion, AccordionSummary, Typography, Tooltip } from '@mui/material';
import { FaCheckCircle } from 'react-icons/fa';
import { GrInProgress } from 'react-icons/gr';
import { RiEdit2Fill } from 'react-icons/ri';
import { CiMenuKebab } from "react-icons/ci";
import { IoIosArrowDropdownCircle } from "react-icons/io";
import SecondLoader from '../../component/SecondLoader';
import { formatRoleLabel, formatRoleLabels, hasAccessManagementRole } from '../../util/UserRoles';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

// ✅ Custom Toolbar with built-in quick filter
const CustomToolbar = ({ quickFilterProps }) => {
    return (
        <div style={{ 
            width: '100%',
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #e8e8e8',
            padding: '8px 16px',
        }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-start', 
                alignItems: 'center',
            }}>
                <GridToolbarQuickFilter
                    {...quickFilterProps}
                    sx={{
                        '& .MuiInputBase-root': {
                            borderRadius: '8px',
                            height: '38px',
                            backgroundColor: 'white',
                            width: '320px',
                        },
                        '& .MuiInputBase-input': {
                            fontSize: '14px',
                            padding: '0 12px',
                        },
                    }}
                    placeholder="Search by Name, Email, or Role..."
                    debounceMs={400}
                    quickFilterParser={(searchInput) => 
                        searchInput.split(',').map((value) => value.trim())
                    }
                />
            </div>
        </div>
    );
};

const Privilege = () => {
    const [users, setUsers] = useState([]);
    const userInfo = useSelector(store => store.user.userInfo);
    const clubId = useSelector(store => store.user.clubId);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [authorizationData, setAuthorizationData] = useState([]);
    const [currentRowData, setCurrentRowData] = useState({
        userId: null,
        userProfileImage: null,
        userFirstName: null,
        userLastName: null,
        userRole: null,
    });
    const [privilegeData, setPrivilegeData] = useState([])
    const [loadingState, setLoadingState] = useState({
        loading1: false,
        loading2: false,
    });
    const [filterData, setFilterData] = useState({});
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [totalUsers, setTotalUsers] = useState(0);
    
    // ✅ Track sorting state with default sort by userFirstName
    const [sortModel, setSortModel] = useState([
        { field: 'userFirstName', sort: 'asc' }
    ]);

    const startLoading = (key) => {
        setLoadingState((prev) => ({
            ...prev,
            [key]: true
        }))
    };

    const finishLoading = (key) => {
        setLoadingState((prev) => ({
            ...prev,
            [key]: false
        }))
    };
    
    const handleClick = (event, userId, userProfileImage, userFirstName, userLastName, userRole) => {
        setAnchorEl(event.currentTarget);
        setCurrentRowData({
            userId: userId,
            userProfileImage: userProfileImage,
            userFirstName: userFirstName,
            userLastName: userLastName,
            userRole: userRole
        });
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // ✅ Handle API call with filter and sorting
    const fetchUsers = async (filterText = '', size, pageNum, sortData = null) => {
        try {
            const finalSize = size !== undefined ? size : paginationModel.pageSize;
            const finalPage = pageNum !== undefined ? pageNum : paginationModel.page;
            
            // ✅ Build sort params from sortModel
            let sortParams = {};
            if (sortData) {
                sortParams = sortData;
            } else if (sortModel && sortModel.length > 0) {
                const sort = sortModel[0];
                const fieldMap = {
                    'userFirstName': 'userFirstName',
                    'userLastName': 'userLastName',
                    'roles': 'roles',
                    'userStatus': 'userStatus',
                    'userEmail': 'userEmail'
                };
                sortParams = {
                    name: fieldMap[sort.field] || sort.field,
                    direction: sort.sort.toUpperCase()
                };
            }
            
            console.log("🔍 Calling API with:", { 
                page: finalPage, 
                size: finalSize, 
                filter: filterText,
                sort: sortParams 
            });
            
            startLoading("loading1");
            const res = await UserAPI.getAllMembersForAClub(
                clubId, 
                finalPage, 
                finalSize, 
                filterText || '', 
                filterData,
                sortParams  // ✅ Pass sort params
            )
            const operators = (res.Data || []).filter((row) => hasAccessManagementRole(row.roles));
            setUsers(operators);
            setTotalUsers(operators.length > 0 ? operators[0]?.totalUser : operators.length);
            finishLoading("loading1");
        } catch (error) {
            finishLoading("loading1");
        }
    }
    
    // ✅ Handle sort model changes
    const handleSortModelChange = useCallback((newSortModel) => {
        setSortModel(newSortModel);
        const filterValue = filterModel?.quickFilterValues?.join(',') || '';
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        
        let sortParams = {};
        if (newSortModel && newSortModel.length > 0) {
            const sort = newSortModel[0];
            const fieldMap = {
                'userFirstName': 'userFirstName',
                'userLastName': 'userLastName',
                'roles': 'roles',
                'userStatus': 'userStatus',
                'userEmail': 'userEmail'
            };
            sortParams = {
                name: fieldMap[sort.field] || sort.field,
                direction: sort.sort.toUpperCase()
            };
        }
        
        fetchUsers(filterValue, paginationModel.pageSize, 0, sortParams);
    }, [paginationModel.pageSize, filterModel]);

    // ✅ Handle filter changes from DataGrid
    const handleFilterChange = useCallback((newFilterModel) => {
        setFilterModel(newFilterModel);
        
        const quickFilterValue = newFilterModel?.quickFilterValues?.join(',') || '';
        
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        
        // Preserve current sort when filtering
        let sortParams = {};
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            const fieldMap = {
                'userFirstName': 'userFirstName',
                'userLastName': 'userLastName',
                'roles': 'roles',
                'userStatus': 'userStatus',
                'userEmail': 'userEmail'
            };
            sortParams = {
                name: fieldMap[sort.field] || sort.field,
                direction: sort.sort.toUpperCase()
            };
        }
        
        fetchUsers(quickFilterValue, paginationModel.pageSize, 0, sortParams);
    }, [paginationModel.pageSize, sortModel]);
    
    const getAllFeatureAndPrivilege = async (userId) => {
        try {
            startLoading("loading2");
            const response = await UserAPI.getAllFeatureAndPrivilege(userId);
            setAuthorizationData(response.Data.featureDTO);
            finishLoading("loading2");
        } catch (error) {
            finishLoading("loading2");
        }
    }
    
    const getAllPrivilege = async () => {
        try {
            const response = await UserAPI.getAllPrivilege();
            setPrivilegeData(response.Data);
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (clubId) {
            getAllPrivilege();
        }
    }, [clubId]);
    
    // ✅ Initial load and pagination changes with sorting
    useEffect(() => {
        if (clubId) {
            const filterValue = filterModel?.quickFilterValues?.join(',') || '';
            
            let sortParams = {};
            if (sortModel && sortModel.length > 0) {
                const sort = sortModel[0];
                const fieldMap = {
                    'userFirstName': 'userFirstName',
                    'userLastName': 'userLastName',
                    'roles': 'roles',
                    'userStatus': 'userStatus',
                    'userEmail': 'userEmail'
                };
                sortParams = {
                    name: fieldMap[sort.field] || sort.field,
                    direction: sort.sort.toUpperCase()
                };
            }
            
            fetchUsers(filterValue, paginationModel.pageSize, paginationModel.page, sortParams);
        }
    }, [clubId, paginationModel.page, paginationModel.pageSize]);

    const getPrivilegeNameById = (subFeatureId, id) => {
        let foundPrivilegeData = privilegeData?.find((pd) => pd.subFeatureId === subFeatureId);
        return foundPrivilegeData?.privileges?.find((privilege) => privilege.privilegeId === id)?.privilegeName;
    }

    const upadtePrivileges = async () => {
        try {
            startLoading("loading2");
            await UserAPI.updatePrivileges(currentRowData?.userId, { featureDTO: authorizationData });
            setOpenDrawer(false);
        } catch (error) {
            console.log(error);
        } finally {
            finishLoading("loading2");
        }
    };

    // ✅ Updated columns with proper widths, sorting, filter disabled
    const columns = useMemo(() => [
        {
            field: 'userProfileImage',
            headerName: 'Profile',
            flex: 0.5,
            minWidth: 70,
            maxWidth: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.row.userFirstName || ''} arrow>
                    <Avatar 
                        src={params.value ? `${import.meta.env.VITE_APP_BASE_URL}/user/user-profile-img/${params.value}` : defaultProfileImg1}
                        sx={{ width: 40, height: 40 }}
                    />
                </Tooltip>
            )
        },
        {
            field: 'userFirstName',
            headerName: 'First Name',
            flex: 1.2,
            minWidth: 140,
            maxWidth: 220,
            sortable: true,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.value || ''} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || ''}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'userLastName',
            headerName: 'Last Name',
            flex: 1.2,
            minWidth: 140,
            maxWidth: 220,
            sortable: true,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.value || ''} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || ''}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'roles',
            headerName: 'Role',
            flex: 1,
            minWidth: 130,
            maxWidth: 200,
            sortable: true,
            filterable: false,
            renderCell: (params) => (
                <div className='sm_display_privilege_name_con'>
                    {params.value?.map((role, index) => (
                        <Chip key={index} label={formatRoleLabel(role)} size="small" sx={{ mr: 0.5 }} />
                    ))}
                </div>
            )
        },
        {
            field: 'userStatus',
            headerName: 'User Status',
            flex: 0.8,
            minWidth: 120,
            maxWidth: 180,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const val = params.value;
                return (
                    <Chip
                        icon={val === 'ACTIVE' ? <FaCheckCircle /> : <GrInProgress />}
                        label={val === 'ACTIVE' ? 'Active' : 'Pending'}
                        color={val === 'ACTIVE' ? 'success' : 'warning'}
                        size="small"
                    />
                );
            }
        },
        {
            field: 'userEmail',
            headerName: 'Email',
            flex: 1.2,
            minWidth: 180,
            maxWidth: 300,
            sortable: true,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.value || ''} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || ''}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'actions',
            headerName: ' ',
            flex: 0.5,
            minWidth: 60,
            maxWidth: 80,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const row = params.row;
                return (
                    <>
                        <div
                            aria-controls={open ? "custom-dropdown" : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? "true" : undefined}
                            onClick={(event) => handleClick(
                                event,
                                row.userId,
                                row.userProfileImage,
                                row.userFirstName,
                                row.userLastName,
                                row.roles
                            )}
                        >
                            <CiMenuKebab className='court_menu_icon' style={{ cursor: 'pointer', fontSize: '20px' }} />
                        </div>
                        <Menu
                            sx={{ left: -55, boxShadow: 'none' }}
                            id="custom-dropdown"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            MenuListProps={{
                                "aria-labelledby": "custom-dropdown-button",
                            }}
                        >
                            <MenuItem onClick={() => {
                                getAllFeatureAndPrivilege(currentRowData?.userId)
                                setOpenDrawer(true)
                                handleClose()
                            }}>
                                <RiEdit2Fill className='court_edit_icon' style={{ marginRight: '8px' }} /> Change Privileges
                            </MenuItem>
                        </Menu>
                    </>
                );
            }
        }
    ], [anchorEl, open, currentRowData]);

    const handlePrivilegeChange = (featureName, subFeatureId, selectedOptions) => {
        setAuthorizationData((prevFeatures) =>
            prevFeatures.map((feature) =>
                feature.featureName === featureName
                    ? {
                        ...feature,
                        subFeatureDTOS: feature.subFeatureDTOS.map((subFeature) =>
                            subFeature.subFeatureId === subFeatureId
                                ? {
                                    ...subFeature,
                                    privilegeDTOS: selectedOptions,
                                }
                                : subFeature
                        ),
                    }
                    : feature
            )
        );
    };

    const rowsWithId = users.map((row, index) => ({
        ...row,
        id: row.userId || `row-${index}`
    }));

    // Check if any loading state is true
    const isLoading = loadingState.loading1;

    return (
        <div className='admin_privilege_container'>
            <div style={{ height: 850, width: '100%' }}>
                <DataGrid
                    rows={rowsWithId}
                    columns={columns}
                    pagination
                    paginationMode="server"
                    rowCount={totalUsers}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(newModel) => {
                        setPaginationModel(newModel);
                    }}
                    pageSizeOptions={[50, 100]}
                    loading={isLoading}
                    disableSelectionOnClick
                    getRowId={(row) => row.id}
                    
                    // ✅ Sorting
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    
                    // ✅ Filter handling
                    filterModel={filterModel}
                    onFilterModelChange={handleFilterChange}
                    
                    // ✅ Ensure column resizing is enabled
                    disableColumnResize={false}
                    
                    slots={{
                        toolbar: () => (
                            <CustomToolbar
                                quickFilterProps={{
                                    debounceMs: 400,
                                }}
                            />
                        ),
                    }}
                    hideFooterPagination={false}
                    sx={{
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f5f5f5',
                            borderBottom: '2px solid #e8e8e8',
                            fontWeight: 600,
                            color: '#333',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                        },
                        // ✅ Clean column separator - let MUI handle resizing
                        '& .MuiDataGrid-columnSeparator': {
                            visibility: 'visible',
                            color: '#d1d1d1',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #f0f0f0',
                            padding: '8px 16px',
                            fontSize: '14px',
                            color: '#333',
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#f5f9ff',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderTop: '1px solid #e8e8e8',
                            padding: '8px 16px',
                            backgroundColor: '#fafafa',
                        },
                        '& .MuiDataGrid-toolbarContainer': {
                            padding: 0,
                        },
                        border: 'none',
                        borderRadius: '8px',
                        // ✅ Hide column menu icon
                        '& .MuiDataGrid-menuIcon': {
                            display: 'none !important',
                        },
                        '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
                            display: 'none !important',
                        },
                        '& .MuiDataGrid-columnHeader:focus': {
                            outline: 'none',
                        },
                    }}
                />
            </div>

            <div>
                <Drawer
                    anchor={'right'}
                    open={openDrawer}
                    onClose={(event, reason) => {
                        if (reason === "backdropClick") return;
                        setOpenDrawer(false);
                    }}
                    sx={{
                        "& .MuiDrawer-paper": {
                            width: "50vw",
                            maxWidth: 800,
                        },
                        "@media (max-width: 700px)": {
                            "& .MuiDrawer-paper": {
                                width: "100vw",
                            },
                        },
                        "@media (min-width:701px) and (max-width: 1000px)": {
                            "& .MuiDrawer-paper": {
                                width: "70vw",
                            },
                        },
                        "@media (min-width:1001px) and (max-width: 1200px)": {
                            "& .MuiDrawer-paper": {
                                width: "60vw",
                            },
                        }
                    }}
                >
                    <div className='admin_privilege_drawer_container'>
                        <h1>Manage Permissions</h1>
                        <div className='admin_privilege_save_cancel_btn'>
                            <div className='admin_privilege_user_info_container'>
                                <img loading="lazy"
                                    src={currentRowData?.userProfileImage ? import.meta.env.VITE_APP_BASE_URL + '/user/user-profile-img/' + currentRowData?.userProfileImage : defaultProfileImg2} alt="profile" />
                                <div className='admin_privilege_user_info'>
                                    <h3>{currentRowData?.userFirstName + " " + currentRowData?.userLastName}</h3>
                                    <p>{formatRoleLabels(currentRowData?.userRole)}</p>
                                </div>
                            </div>
                            <div className='update_privileges_btn_container'>
                                <Button variant='contained' onClick={upadtePrivileges}>Save</Button>
                                <Button
                                    color='error'
                                    variant='outlined'
                                    onClick={() => {
                                        setOpenDrawer(false)
                                        setCurrentRowData(null);
                                    }}
                                >Cancel</Button>
                            </div>
                        </div>

                        <div className='admin_feature_privilege_accordion_container'>
                            {loadingState.loading2 ?
                                <SecondLoader />
                                :
                                <>
                                    {authorizationData?.map((feature, index) =>
                                        <Accordion key={index}>
                                            <AccordionSummary
                                                expandIcon={<IoIosArrowDropdownCircle
                                                    style={{
                                                        color: 'var(--hoverColorBold)',
                                                        fontSize: 22
                                                    }} />}
                                                sx={{
                                                    borderBottom: '1px solid rgba(0, 0, 0, 0.205)'
                                                }}
                                                aria-controls="panel1-content"
                                                id="panel1-header"
                                            >
                                                <Typography sx={{
                                                    fontWeight: 500,
                                                    textTransform: 'capitalize',
                                                    fontSize: 20
                                                }} component="span">
                                                    {feature?.featureName?.replace('_', ' ').toLowerCase()}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                {feature?.subFeatureDTOS?.map((subFeature, subIndex) => <div key={subIndex}>
                                                    <div className='privilege_sub_feature'>
                                                        <h2>{subFeature?.subFeatureName?.replaceAll('_', ' ').toLowerCase()}</h2>
                                                        <div className='privilege_sub_feature_multi_select'>
                                                            <FormControl sx={{ width: '100%' }}
                                                                className="privilege-edit-input"
                                                            >
                                                                <InputLabel id={`demo-multiple-chip-label-${subFeature?.subFeatureId}`}>Privileges</InputLabel>
                                                                <Select
                                                                    labelId={`demo-multiple-chip-label-${subFeature?.subFeatureId}`}
                                                                    id={`demo-multiple-chip-${subFeature?.subFeatureId}`}
                                                                    multiple
                                                                    sx={{
                                                                        minWidth: 300,
                                                                        "@media (max-width: 700px)": {
                                                                            minWidth: 0
                                                                        },
                                                                    }}
                                                                    value={subFeature?.privilegeDTOS?.map((privilege) => privilege?.privilegeId) || []}
                                                                    size='medium'
                                                                    onChange={(event) =>
                                                                        handlePrivilegeChange(
                                                                            feature?.featureName,
                                                                            subFeature?.subFeatureId,
                                                                            event.target.value.map((val) => {
                                                                                return { privilegeId: val, privilegeName: getPrivilegeNameById(subFeature?.subFeatureId, val) }
                                                                            })
                                                                        )}
                                                                    input={<OutlinedInput id={`select-multiple-chip-${subFeature?.subFeatureId}`} label="Privileges" />}
                                                                    renderValue={(selected) => (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                            {selected.map((value) => (
                                                                                <Chip sx={{
                                                                                    backgroundColor: 'var(--hoverColorBold)',
                                                                                    color: 'black',
                                                                                    fontWeight: 500
                                                                                }} key={value} label={getPrivilegeNameById(subFeature?.subFeatureId, value)} />
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                    MenuProps={MenuProps}
                                                                >
                                                                    {privilegeData?.flatMap((pd) =>
                                                                        pd?.subFeatureId === subFeature?.subFeatureId
                                                                            ? pd?.privileges?.map((value) => (
                                                                                <MenuItem
                                                                                    key={value.privilegeId}
                                                                                    value={value.privilegeId}
                                                                                >
                                                                                    {value.privilegeName}
                                                                                </MenuItem>
                                                                            ))
                                                                            : []
                                                                    )}
                                                                </Select>
                                                            </FormControl>
                                                        </div>
                                                    </div>
                                                </div>)}
                                            </AccordionDetails>
                                        </Accordion>
                                    )}
                                </>}
                        </div>
                    </div>
                </Drawer>
            </div>
        </div>
    )
}

export default Privilege